// components/EditLeadModal.jsx
import React, { useState, useEffect } from "react";
import { X, Building2, User, DollarSign, Calendar, Target } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditLeadModal = ({ lead, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [activeSection, setActiveSection] = useState("basic");
  const [formData, setFormData] = useState({
    // Basic Information
    salutation: lead.salutation || "",
    firstName: lead.firstName || "",
    lastName: lead.lastName || "",
    fullName: lead.fullName || "",
    email: lead.email || "",
    phone: lead.phone || "",
    mobilePhone: lead.mobilePhone || "",
    workPhone: lead.workPhone || "",
    fax: lead.fax || "",

    // Company Information
    companyName: lead.companyName || "",
    companyWebsite: lead.companyWebsite || "",
    companyIndustry: lead.companyIndustry || "",
    companySize: lead.companySize || "",
    numberOfEmployees: lead.numberOfEmployees || "",
    annualRevenue: lead.annualRevenue || "",

    // Professional Information
    jobTitle: lead.jobTitle || "",
    department: lead.department || "",
    seniority: lead.seniority || "",

    // Location Information
    street: lead.street || "",
    city: lead.city || "",
    state: lead.state || "",
    postalCode: lead.postalCode || "",
    country: lead.country || "",

    // Social Media
    linkedinUrl: lead.linkedinUrl || "",
    twitterUrl: lead.twitterUrl || "",
    facebookUrl: lead.facebookUrl || "",

    // Lead Management
    leadStatus: lead.leadStatus || "new",
    rating: lead.rating || "Cold",
    leadSource: lead.leadSource || "",
    score: lead.score || "",

    // Financial Information
    amount: lead.amount || "",
    currency: lead.currency || "GBP",
    probability: lead.probability || 50,
    expectedRevenue: lead.expectedRevenue || "",
    expectedCloseDate: lead.expectedCloseDate?.split("T")[0] || "",

    // Opportunity Information
    opportunityStage: lead.opportunityStage || "",
    opportunityPriority: lead.opportunityPriority || "medium",
    opportunityType: lead.opportunityType || "",
    pipeline: lead.pipeline || "sales",
    nextStep: lead.nextStep || "",
    primaryCampaignSource: lead.primaryCampaignSource || "",

    // Requirements
    numberOfHandsets: lead.numberOfHandsets || "",
    monthlySpendWithCurrentProvider: lead.monthlySpendWithCurrentProvider || "",
    currentPhoneSystemAge: lead.currentPhoneSystemAge || "",
    unitsSold: lead.unitsSold || "",
    contractTermLeft: lead.contractTermLeft || "",
    contractEndDate: lead.contractEndDate?.split("T")[0] || "",
    callerNotes: lead.callerNotes || "",

    // Deal Score
    booked: lead.booked || false,
    bookedDate: lead.bookedDate?.split("T")[0] || "",
    twoMinChallenge: lead.twoMinChallenge || false,
    twoMinChallengeDate: lead.twoMinChallengeDate?.split("T")[0] || "",
    nonQualSat: lead.nonQualSat || false,
    satNonQualDate: lead.satNonQualDate?.split("T")[0] || "",
    qualSat: lead.qualSat || false,
    qualSatDate: lead.qualSatDate?.split("T")[0] || "",
    sold: lead.sold || false,
    soldDate: lead.soldDate?.split("T")[0] || "",
    installComplete: lead.installComplete || false,
    installationDate: lead.installationDate?.split("T")[0] || "",
    notSat: lead.notSat || false,

    // Additional Information
    orderNumber: lead.orderNumber || "",
    currentGenerators: lead.currentGenerators || "",
    trackingNumber: lead.trackingNumber || "",
    mainCompetitors: lead.mainCompetitors || "",
    deliveryInstallationStatus: lead.deliveryInstallationStatus || "",

    // Other
    description: lead.description || "",
    doNotCall: lead.doNotCall || false,
    emailOptOut: lead.emailOptOut || false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const sanitised = Object.fromEntries(
        Object.entries(formData).map(([key, val]) => [
          key,
          val === "" ? null : val,
        ]),
      );

      const updateData = {
        ...sanitised,
        numberOfEmployees: sanitised.numberOfEmployees
          ? parseInt(sanitised.numberOfEmployees)
          : null,
        annualRevenue: sanitised.annualRevenue
          ? parseFloat(sanitised.annualRevenue)
          : null,
        score: sanitised.score ? parseInt(sanitised.score) : null,
        amount: sanitised.amount ? parseFloat(sanitised.amount) : null,
        probability: sanitised.probability
          ? parseInt(sanitised.probability)
          : null,
        expectedRevenue: sanitised.expectedRevenue
          ? parseFloat(sanitised.expectedRevenue)
          : null,
        numberOfHandsets: sanitised.numberOfHandsets
          ? parseInt(sanitised.numberOfHandsets)
          : null,
        monthlySpendWithCurrentProvider:
          sanitised.monthlySpendWithCurrentProvider
            ? parseFloat(sanitised.monthlySpendWithCurrentProvider)
            : null,
        currentPhoneSystemAge: sanitised.currentPhoneSystemAge
          ? parseFloat(sanitised.currentPhoneSystemAge)
          : null,
        unitsSold: sanitised.unitsSold ? parseFloat(sanitised.unitsSold) : null,
        contractTermLeft: sanitised.contractTermLeft
          ? parseFloat(sanitised.contractTermLeft)
          : null,
      };

      await apiService.updateLead(lead.id, updateData);
      onSuccess();
    } catch (error) {
      console.error("Error updating lead:", error);
      setError(error.response?.data?.message || "Failed to update lead");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sections = [
    { id: "basic", label: "Basic Information", icon: User },
    { id: "company", label: "Company", icon: Building2 },
    { id: "financial", label: "Financial", icon: DollarSign },
    { id: "requirements", label: "Requirements", icon: Target },
    { id: "dealScore", label: "Deal Score", icon: Target },
    { id: "additional", label: "Additional", icon: Target },
  ];

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    return symbols[currency] || currency;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-4xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between z-10 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Edit Lead
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Section Tabs */}
        <div
          className={`border-b ${
            isDarkMode
              ? "border-gray-700 bg-[#13161B]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex overflow-x-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                    activeSection === section.id
                      ? isDarkMode
                        ? "text-blue-400 border-b-2 border-blue-400"
                        : "text-blue-600 border-b-2 border-blue-600"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} />
                    {section.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div
              className={`px-4 py-3 rounded-lg ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          {/* Basic Information */}
          {activeSection === "basic" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Salutation
                  </label>
                  <select
                    name="salutation"
                    value={formData.salutation}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Mobile Phone
                  </label>
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Work Phone
                  </label>
                  <input
                    type="tel"
                    name="workPhone"
                    value={formData.workPhone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Job Title
                  </label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Lead Status
                  </label>
                  <select
                    name="leadStatus"
                    value={formData.leadStatus}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="unqualified">Unqualified</option>
                    <option value="lead_pot">Lead Pot</option>
                    <option value="new">New</option>
                    <option value="nurture">Nurture</option>
                    <option value="converted">Converted</option>
                    <option value="dead">Dead</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Rating
                  </label>
                  <select
                    name="rating"
                    value={formData.rating}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="Cold">Cold</option>
                    <option value="Warm">Warm</option>
                    <option value="Hot">Hot</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Lead Source
                  </label>
                  <input
                    type="text"
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="e.g., Web, Phone, Email"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Score (0-100)
                  </label>
                  <input
                    type="number"
                    name="score"
                    value={formData.score}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="doNotCall"
                      checked={formData.doNotCall}
                      onChange={handleChange}
                      className={`rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    Do Not Call
                  </label>
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="emailOptOut"
                      checked={formData.emailOptOut}
                      onChange={handleChange}
                      className={`rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    Email Opt Out
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Company Information */}
          {activeSection === "company" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Company Website
                  </label>
                  <input
                    type="url"
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Industry
                  </label>
                  <input
                    type="text"
                    name="companyIndustry"
                    value={formData.companyIndustry}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Company Size
                  </label>
                  <input
                    type="text"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Number of Employees
                  </label>
                  <input
                    type="number"
                    name="numberOfEmployees"
                    value={formData.numberOfEmployees}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Annual Revenue (£)
                  </label>
                  <input
                    type="number"
                    name="annualRevenue"
                    value={formData.annualRevenue}
                    onChange={handleChange}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <h4
                    className={`text-sm font-semibold mb-3 mt-4 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Location
                  </h4>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Street
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <h4
                    className={`text-sm font-semibold mb-3 mt-4 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Social Media
                  </h4>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    LinkedIn URL
                  </label>
                  <input
                    type="url"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Twitter URL
                  </label>
                  <input
                    type="url"
                    name="twitterUrl"
                    value={formData.twitterUrl}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    name="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Financial Information */}
          {activeSection === "financial" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Financial Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Expected Amount
                  </label>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      step="0.01"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Win Probability (%)
                  </label>
                  <input
                    type="number"
                    name="probability"
                    value={formData.probability}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Expected Revenue
                  </label>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="expectedRevenue"
                      value={formData.expectedRevenue}
                      onChange={handleChange}
                      step="0.01"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Expected Close Date
                  </label>
                  <input
                    type="date"
                    name="expectedCloseDate"
                    value={formData.expectedCloseDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Opportunity Stage
                  </label>
                  <select
                    name="opportunityStage"
                    value={formData.opportunityStage}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="">--None--</option>
                    <option value="qualification">Qualification</option>
                    <option value="needs_analysis">Needs Analysis</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed_won">Closed Won</option>
                    <option value="closed_lost">Closed Lost</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Priority
                  </label>
                  <select
                    name="opportunityPriority"
                    value={formData.opportunityPriority}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Opportunity Type
                  </label>
                  <input
                    type="text"
                    name="opportunityType"
                    value={formData.opportunityType}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="e.g., New Business, Renewal"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Pipeline
                  </label>
                  <select
                    name="pipeline"
                    value={formData.pipeline}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="sales">Sales</option>
                    <option value="renewal">Renewal</option>
                    <option value="upsell">Upsell</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Next Step
                  </label>
                  <input
                    type="text"
                    name="nextStep"
                    value={formData.nextStep}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Primary Campaign Source
                  </label>
                  <input
                    type="text"
                    name="primaryCampaignSource"
                    value={formData.primaryCampaignSource}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Requirements */}
          {activeSection === "requirements" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Requirements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Number of Handsets / Phone Extensions
                  </label>
                  <input
                    type="number"
                    name="numberOfHandsets"
                    value={formData.numberOfHandsets}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Monthly Spend with Current Provider
                  </label>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="monthlySpendWithCurrentProvider"
                      value={formData.monthlySpendWithCurrentProvider}
                      onChange={handleChange}
                      step="0.01"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Current Phone System Age (Years)
                  </label>
                  <input
                    type="number"
                    name="currentPhoneSystemAge"
                    value={formData.currentPhoneSystemAge}
                    onChange={handleChange}
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Units Sold
                  </label>
                  <input
                    type="number"
                    name="unitsSold"
                    value={formData.unitsSold}
                    onChange={handleChange}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contract Term Left (Years)
                  </label>
                  <input
                    type="number"
                    name="contractTermLeft"
                    value={formData.contractTermLeft}
                    onChange={handleChange}
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contract End Date
                  </label>
                  <input
                    type="date"
                    name="contractEndDate"
                    value={formData.contractEndDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Caller Notes
                  </label>
                  <textarea
                    name="callerNotes"
                    value={formData.callerNotes}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="Enter caller notes..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Deal Score */}
          {activeSection === "dealScore" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Deal Score
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="booked"
                      checked={formData.booked}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">BOOKED</span>
                  </label>
                  {formData.booked && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Booked Date
                      </label>
                      <input
                        type="date"
                        name="bookedDate"
                        value={formData.bookedDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="twoMinChallenge"
                      checked={formData.twoMinChallenge}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">2 MIN CHALLENGE</span>
                  </label>
                  {formData.twoMinChallenge && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        2 MIN CHALLENGE Date
                      </label>
                      <input
                        type="date"
                        name="twoMinChallengeDate"
                        value={formData.twoMinChallengeDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="nonQualSat"
                      checked={formData.nonQualSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">NON QUAL SAT</span>
                  </label>
                  {formData.nonQualSat && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        SAT NON-QUAL Date
                      </label>
                      <input
                        type="date"
                        name="satNonQualDate"
                        value={formData.satNonQualDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="qualSat"
                      checked={formData.qualSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">QUAL SAT</span>
                  </label>
                  {formData.qualSat && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        QUAL SAT Date
                      </label>
                      <input
                        type="date"
                        name="qualSatDate"
                        value={formData.qualSatDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="sold"
                      checked={formData.sold}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">SOLD</span>
                  </label>
                  {formData.sold && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Sold Date
                      </label>
                      <input
                        type="date"
                        name="soldDate"
                        value={formData.soldDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="installComplete"
                      checked={formData.installComplete}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">Install Complete</span>
                  </label>
                  {formData.installComplete && (
                    <div className="mt-2">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Installation Date
                      </label>
                      <input
                        type="date"
                        name="installationDate"
                        value={formData.installationDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="notSat"
                      checked={formData.notSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">NOT SAT</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          {activeSection === "additional" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Additional Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Order Number
                  </label>
                  <input
                    type="text"
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Current Generators
                  </label>
                  <input
                    type="text"
                    name="currentGenerators"
                    value={formData.currentGenerators}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Tracking Number
                  </label>
                  <input
                    type="text"
                    name="trackingNumber"
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Delivery/Installation Status
                  </label>
                  <select
                    name="deliveryInstallationStatus"
                    value={formData.deliveryInstallationStatus}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="delayed">Delayed</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Main Competitors
                  </label>
                  <textarea
                    name="mainCompetitors"
                    value={formData.mainCompetitors}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="Enter main competitors..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            className={`flex items-center gap-3 pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {loading ? "Updating..." : "Update Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLeadModal;
